<template>
	<div>
		<v-btn
			v-test="'current-location-filter'"
			class="filter-item"
			:class="{ active: isLocationFilterEnabled }"
			:disabled="props.disabled"
			elevation="2"
			@click="toggleLocationFilter">
			Current Location
			<v-icon>icon-map-pin-stroke</v-icon>
		</v-btn>

		<v-dialog v-model="showPermissionHelpDialog" max-width="420px" offset-y>
			<v-card class="ma-0 w-100 pa-4" rounded="xl" elevation="8">
				<v-card-text>
					<PermissionHelp
						id="permission-help-dialog"
						ref="permissionHelpRef"
						:show-permission-help="showPermissionHelp"
						:error="locationErrorMessage"
						@cancel="closeLocationHelpDialog()"
						@try-again="setLocationFilter(true)" />
				</v-card-text>
			</v-card>
		</v-dialog>

		<!-- Loading dialog. The geolocation request can hang up to the full timeout (e.g. Location
		     Services toggled off mid-session, which WebKit surfaces only as a timeout). Show a spinner,
		     and after a short CSS-driven delay reveal a Cancel so the user is never stuck waiting. -->
		<v-dialog v-model="isLoadingLocation" max-width="420px" persistent>
			<v-card class="ma-0 w-100 pa-4 text-center gps-filter-loading-card" rounded="xl" elevation="8">
				<v-progress-circular indeterminate color="primary" class="mb-2" />
				<div class="text-body-1">Getting your location…</div>
				<div v-if="showSlowEscape" class="gps-filter-slow-escape mt-3">
					<div class="text-body-2 mb-2" style="color: rgba(0, 0, 0, 0.6)">This is taking longer than expected.</div>
					<v-btn class="gps-filter-slow-cancel" variant="outlined" rounded="xl" size="small" @click="cancelLocationRequest">
						Cancel
					</v-btn>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>

<script setup lang="ts">
	import DeviceUtility from "@/helpers/device";
	import { getCurrentLocation, UserLocation } from "@/helpers/location";
	import { logInfo } from "@/helpers/logError";
	import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
	import PermissionHelp from "@/components/location/PermissionHelp.vue";

	const emit = defineEmits<(e: "filter-coordinates", coordinates: UserLocation | null) => void>();

	function restore() {
		if (props.preSelectedLatitude && props.preSelectedLongitude) {
			isLocationFilterEnabled.value = true;
		}
	}

	defineExpose({
		toggleLocationFilter,
		clearLocationFilter: clear,
		restore
	});

	const props = defineProps<{
		disabled: boolean;
		preSelectedLatitude?: number | null;
		preSelectedLongitude?: number | null;
	}>();

	onBeforeUnmount(() => {
		// Clean up event listener when component is destroyed
		document.removeEventListener("click", handleClickOutside);
	});
	onMounted(() => {
		if (props.preSelectedLatitude && props.preSelectedLongitude) {
			isLocationFilterEnabled.value = true;
		}
		document.addEventListener("click", handleClickOutside);
	});

	const showPermissionHelpDialog = computed(() => {
		return showPermissionHelp.value || locationErrorMessage.value !== "";
	});

	const isLocationFilterEnabled = ref<boolean>(false);
	const isLoadingLocation = ref<boolean>(false);
	const showPermissionHelp = ref<boolean>(false);
	const locationErrorMessage = ref<string>("");
	const permissionHelpRef = ref<InstanceType<typeof PermissionHelp> | null>(null);

	// "Taking longer than expected" + Cancel, revealed after 3.5s of waiting. Driven by a JS timer
	// (not a CSS animation, which is unreliable on iOS WebKit) so it appears on every browser.
	const showSlowEscape = ref<boolean>(false);
	let slowEscapeTimer: ReturnType<typeof setTimeout> | null = null;

	watch(isLoadingLocation, (loading) => {
		if (slowEscapeTimer) clearTimeout(slowEscapeTimer);
		slowEscapeTimer = null;
		showSlowEscape.value = false;
		if (loading) {
			slowEscapeTimer = setTimeout(() => (showSlowEscape.value = true), 3500);
		}
	});

	onBeforeUnmount(() => {
		if (slowEscapeTimer) clearTimeout(slowEscapeTimer);
	});

	async function toggleLocationFilter() {
		if (isLocationFilterEnabled.value) {
			clear(true);
		} else {
			await setLocationFilter();
		}
	}

	// Identifies the in-flight request so a result that arrives after the user cancels (the 15s
	// geolocation wait cannot be aborted) is ignored instead of popping the error dialog late.
	let activeLocationRequestId = 0;

	function cancelLocationRequest() {
		activeLocationRequestId++;
		isLoadingLocation.value = false;
	}

	async function setLocationFilter(tryAgain: boolean = false) {
		if (isLoadingLocation.value) return false;
		const myRequestId = activeLocationRequestId;
		isLoadingLocation.value = true;
		locationErrorMessage.value = "";
		showPermissionHelp.value = false;

		const autoRedirect = () => {
			// Reload the page and auto redirect to the address lookup after the user updates their permission
			let url = window.location.href;
			window.location.href = url;
		};

		let permissionState: PermissionState | "unsupported" = "unsupported";
		try {
			if (navigator.permissions?.query) {
				const status = await navigator.permissions.query({ name: "geolocation" as PermissionName });
				permissionState = status.state;
			}
		} catch (permissionsError) {
			// Some browsers (e.g. iOS WebKit) reject permissions.query for "geolocation". That is not a
			// failure of the location flow — we fall through to getCurrentLocation — but leave a breadcrumb
			// so the pre-flight check being skipped is visible in Datadog (silent-failure rule).
			permissionState = "unsupported";
			logInfo("GpsLocationFilter: permissions.query unavailable, skipping pre-flight check", {
				feature: "gps-location-filter",
				permissionState: "unsupported",
				error: permissionsError instanceof Error ? permissionsError.message : String(permissionsError)
			});
		}

		if (myRequestId !== activeLocationRequestId) return false;

		// Skip the pre-flight short-circuit on an iOS "Try Again": iOS can report a stale
		// "denied" from permissions.query until a full-page reload, and the reload only runs
		// in the catch block below. Falling through there preserves that recovery path.
		if (permissionState === "denied" && !(DeviceUtility.isIosOrIpados && tryAgain)) {
			showPermissionHelp.value = true;
			logInfo("GpsLocationFilter: geolocation permission denied before current location request", {
				feature: "gps-location-filter",
				permissionState: "denied"
			});
			isLoadingLocation.value = false;
			return false;
		}

		try {
			const location = await getCurrentLocation(
				{ enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
				{ feature: "gps-location-filter", permissionState }
			);
			// Ignore a result that lands after the user cancelled the wait.
			if (myRequestId !== activeLocationRequestId) return false;
			emit("filter-coordinates", location);
			isLocationFilterEnabled.value = true;
		} catch (error: any) {
			// Ignore a failure that lands after the user cancelled — do not pop the error dialog late.
			if (myRequestId !== activeLocationRequestId) return false;
			// get error message if there is one
			const errorMessage = error.message || "Unknown error";
			locationErrorMessage.value = errorMessage;
			// Reload the page and auto redirect to the address lookup after the user updates their permission since
			// the update does not take immediate effect on iOS. Note: It's not guaranteed that the user actually updated
			// their permissions so if they click 'Try Again', they may end up in the same scenario.
			if (DeviceUtility.isIosOrIpados && tryAgain && errorMessage.includes("denied")) {
				autoRedirect();
			}

			if (errorMessage.includes("denied")) {
				showPermissionHelp.value = true;
				return false;
			}
			return false;
		} finally {
			// Only the current request clears the loading flag; a cancelled one already did.
			if (myRequestId === activeLocationRequestId) isLoadingLocation.value = false;
		}
	}

	async function clear(emitEvent: boolean = false) {
		if (emitEvent) emit("filter-coordinates", null);
		isLocationFilterEnabled.value = false;
	}

	function closeLocationHelpDialog() {
		showPermissionHelp.value = false;
		locationErrorMessage.value = "";
	}
	// TODO: update this to use the directive or just fix vuetify to close the dialog when clicking outside
	function handleClickOutside(event: MouseEvent) {
		const menu = permissionHelpRef.value?.$el;
		const permissionMenuRef = permissionHelpRef.value;

		const targetElement = event.target as HTMLElement;
		const containsResult = menu?.contains(targetElement);

		if (permissionMenuRef && !containsResult) {
			closeLocationHelpDialog();
		}
	}
</script>
